import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import {
  collection, doc, onSnapshot, orderBy, limit, query, updateDoc, where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useThemeColors } from '../context/ThemeContext';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useTopBarConfig } from '../context/TopBarContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { useTranslation } from 'react-i18next';
import { timeAgo } from '../utils/formatters';
import { resolveNotifThumbs, notifTypeMeta } from '../utils/notifThumb';
import { Bell, Sun, Moon } from 'lucide-react';

// Rutas donde cada rol ve la barra de arriba.
//
// 🔴 `/marketplace` tiene que estar en LAS TRES. El buscador del Marketplace no
// vive en la página: vive aquí, en el `secondRow` que registra `useTopBar`. Sin
// esta ruta en la lista, el taller y el mecánico entran al Marketplace —que es
// una entrada de su propio menú, ver `utils/navItems.js`— y no tienen dónde
// buscar, ni carrito, ni el botón «Gestionar pedidos» que `Marketplace.js` arma
// justo para ellos. La página, además, sigue reservando el hueco de la barra.
// Como el contexto activo se guarda en `localStorage` (por aparato), la misma
// cuenta lo veía bien en un teléfono y sin buscador en el otro.
const CLIENT_PATHS = ['/home', '/vehicles', '/marketplace', '/asistencia'];
const MECHANIC_PATHS = ['/mechanic/dashboard', '/vehicles', '/mechanic/appointments', '/mechanic/setup', '/marketplace'];
const WORKSHOP_PATHS = ['/workshop/dashboard', '/vehicles', '/workshop/appointments', '/workshop/store', '/marketplace'];

export default function MobileTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveRole } = useAuth();
  const { isDark, toggleDark } = useThemeColors();
  const isDesktop = useIsDesktop();
  const config = useTopBarConfig();
  const { t } = useTranslation();
  const tokens = isDark ? dsv2 : dsv2Light;

  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [thumbs, setThumbs] = useState({});
  const triedThumb = useRef(new Set());
  const notifRef = useRef(null);

  useEffect(() => {
    if (!auth.currentUser) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', auth.currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
    return onSnapshot(q, (snap) => {
      const notifs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setNotifications(notifs);
      setUnreadCount(notifs.filter((n) => !n.read).length);
    }, (err) => { if (err?.code !== 'permission-denied') console.error('MobileTopBar snapshot:', err); });
  }, []);

  // Miniaturas (estilo 02): resuelve la imagen de cada notif una sola vez.
  useEffect(() => {
    const pending = notifications.filter((n) => !triedThumb.current.has(n.id));
    if (!pending.length) return;
    pending.forEach((n) => triedThumb.current.add(n.id));
    resolveNotifThumbs(pending).then((m) => {
      if (m && Object.keys(m).length) setThumbs((prev) => ({ ...prev, ...m }));
    });
  }, [notifications]);

  useEffect(() => {
    const handle = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    if (showNotifs) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showNotifs]);

  if (isDesktop) return null;

  const role = effectiveRole;
  const mainPaths = role === 'workshop_owner' ? WORKSHOP_PATHS
    : role === 'mechanic' ? MECHANIC_PATHS
    : CLIENT_PATHS;
  if (!mainPaths.includes(location.pathname)) return null;

  const logoSrc = '/mekafix-logo-v3.png';

  const markAsRead = async (id) => {
    await updateDoc(doc(db, 'notifications', id), { read: true, readAt: new Date() }).catch(() => {});
  };
  const markAllRead = async () => {
    await Promise.all(notifications.filter((n) => !n.read).map((n) =>
      updateDoc(doc(db, 'notifications', n.id), { read: true, readAt: new Date() }).catch(() => {}),
    ));
  };
  const handleNotifClick = async (n) => {
    await markAsRead(n.id);
    setShowNotifs(false);
    if (n.link) navigate(n.link);
  };
  const renderNotifThumb = (notif) => {
    const url = thumbs[notif.id];
    const { Icon, color } = notifTypeMeta(notif.type, tokens);
    if (url) {
      const rowBg = notif.read ? tokens.surface : tokens.surfaceLow;
      return (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <img
            src={url} alt=""
            style={{ width: 40, height: 40, borderRadius: 11, objectFit: 'cover', display: 'block', border: `1px solid ${tokens.outline}`, background: tokens.surfaceHigh }}
          />
          <div style={{
            position: 'absolute', right: -3, bottom: -3,
            width: 17, height: 17, borderRadius: '50%', background: color,
            border: `2px solid ${rowBg}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={10} color="#fff" strokeWidth={2.4} />
          </div>
        </div>
      );
    }
    return (
      <div style={{
        width: 40, height: 40, borderRadius: 11, flexShrink: 0,
        background: color + '1A', border: `1px solid ${tokens.outline}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} color={color} />
      </div>
    );
  };

  const leftElement = config?.leftSlot !== undefined
    ? config.leftSlot
    : (config?.centerSlot !== undefined ? config.centerSlot : null);

  /**
   * 🔴 EL LOGO LE CEDE EL SITIO AL TÍTULO DE LA PÁGINA.
   *
   * El logo iba `position:absolute` centrado con hasta 116px de ancho, y las dos
   * mitades de la barra se repartían el resto **por igual** (`flex:1` a cada
   * lado) reservando 60px de padding cada una. Consecuencia medida a 390px: la
   * caja del `<h1>` quedaba en **123px** para un texto que necesita **132px**,
   * así que «Panel del Taller» salía «Panel del Tall» pegado al logo, y a 360px
   * la caja bajaba a 108px. No era un caso raro: le pasaba a TODAS las
   * cabeceras del taller, empezando por la primera pantalla que ve un negocio
   * recién registrado.
   *
   * La regla es una sola y no lleva números mágicos: **la barra enseña la marca
   * o enseña dónde estás, nunca las dos cosas peleando por el mismo centro**.
   * Cuando la página registra su propia cabecera (`useTopBar`), esa cabecera es
   * la identidad de la pantalla y el logo se retira; cuando no la registra, la
   * barra vuelve a ser la de la marca, centrada como siempre.
   *
   * Esconderlo solo por debajo de cierto ancho no servía: a 320px el título
   * tampoco cabe con el logo encogido, y un umbral deja el fallo vivo en los
   * teléfonos donde más duele.
   */
  const showLogo = !leftElement;

  const bellButton = (
    <div ref={notifRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setShowNotifs((v) => !v)}
        style={{
          position: 'relative',
          width: 36, height: 36,
          borderRadius: tokens.radius.pill,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'grid', placeItems: 'center',
          color: tokens.text,
        }}
      >
        <Bell size={20} strokeWidth={1.8} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: 4, right: 4,
            minWidth: 16, height: 16, padding: '0 4px',
            background: tokens.red, color: '#fff',
            borderRadius: 999,
            border: `2px solid ${tokens.surfaceLow}`,
            fontSize: 9, fontWeight: 700,
            fontFamily: tokens.font.mono,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1,
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showNotifs && (
        <div style={{
          position: 'absolute', top: 44, right: 0,
          width: 'min(360px, calc(100vw - 32px))',
          background: tokens.surface,
          border: `1px solid ${tokens.outline}`,
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.shadow.lg,
          zIndex: 1000, overflow: 'hidden',
          transformOrigin: 'top right',
          animation: 'bellOpen .22s cubic-bezier(.22,1,.36,1)',
        }}>
          <div style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${tokens.outline}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{
              fontWeight: 700, fontSize: 15,
              color: tokens.text,
              fontFamily: tokens.font.head,
            }}>
              {t('inbox.title')}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  style={{
                    padding: '4px 10px', background: 'none', border: 'none',
                    color: tokens.red, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {t('inbox.markAllRead')}
                </button>
              )}
              <button
                onClick={() => { setShowNotifs(false); navigate('/inbox'); }}
                style={{
                  padding: '4px 10px',
                  background: tokens.surfaceLow,
                  border: 'none',
                  borderRadius: tokens.radius.md,
                  color: tokens.textMuted,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('home.viewAll')}
              </button>
            </div>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: tokens.textMuted }}>
                <Bell size={32} color={tokens.textDim} style={{ marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 14 }}>{t('home.noNotifications')}</p>
              </div>
            ) : notifications.slice(0, 10).map((notif) => (
              <div
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${tokens.outline}`,
                  cursor: 'pointer',
                  background: notif.read ? tokens.surface : tokens.surfaceLow,
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                }}
              >
                {renderNotifThumb(notif)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: notif.read ? 400 : 600,
                    color: tokens.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {notif.title}
                  </div>
                  <div style={{
                    fontSize: 12, color: tokens.textMuted,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {notif.body || notif.message}
                  </div>
                  <div style={{
                    fontSize: 11, color: tokens.textDim, marginTop: 2,
                    fontFamily: tokens.font.mono,
                  }}>
                    {notif.createdAt?.toDate?.() ? timeAgo(notif.createdAt.toDate()) : ''}
                  </div>
                </div>
                {!notif.read && (
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: tokens.red, flexShrink: 0, marginTop: 6,
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <header
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 100,
        paddingTop: 'env(safe-area-inset-top)',
        background: isDark ? 'rgba(16,20,22,0.82)' : 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${tokens.outline}`,
      }}
    >
      <div style={{
        height: 52, padding: '0 12px',
        display: 'flex', alignItems: 'center', position: 'relative',
      }}>
        <div
          data-testid="topbar-left"
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, paddingRight: showLogo ? 60 : 8 }}
        >
          {leftElement}
        </div>
        {showLogo && (
        <div style={{
          position: 'absolute', left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}>
          <img
            src={logoSrc}
            alt="MekaFix"
            onClick={() => navigate('/home')}
            style={{ height: 34, width: 'auto', maxWidth: 116, display: 'block', pointerEvents: 'auto', cursor: 'pointer' }}
          />
        </div>
        )}
        {/* Sin logo, la mitad derecha ocupa lo que ocupan sus botones y ni un
            pixel más: repartir a partes iguales era lo que estrangulaba al
            título aunque el logo ya no estuviera en medio. */}
        <div
          data-testid="topbar-right"
          style={{ flex: showLogo ? 1 : '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, paddingLeft: showLogo ? 60 : 8 }}
        >
          {config?.rightSlot}
          {config?.showDarkToggle && (
            <button
              onClick={toggleDark}
              style={{
                width: 36, height: 36,
                borderRadius: tokens.radius.pill,
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'grid', placeItems: 'center',
                color: tokens.text,
              }}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          )}
          {!config?.hideBell && bellButton}
        </div>
      </div>
      {config?.secondRow && (
        <div style={{ padding: '0 16px 10px' }}>{config.secondRow}</div>
      )}
    </header>
  );
}
